import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Project, deleteProject, startProject } from '../api/projects'

interface ProjectCardProps {
  project: Project
  onDelete?: (projectId: number) => void
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  waiting_input: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
}

const statusLabels: Record<string, string> = {
  draft: 'Черновик',
  pending: 'В очереди',
  processing: 'Обработка',
  waiting_input: 'Ожидает ввода',
  completed: 'Завершён',
  failed: 'Ошибка',
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const formatDuration = (seconds: number | null) => {
  if (!seconds) return null
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [starting, setStarting] = useState(false)

  const isCompleted = project.status === 'completed'
  const isDraft = project.status === 'draft'
  const hasSources = project.source_count > 0

  const handleDelete = async () => {
    if (!confirm('Удалить проект? Это действие нельзя отменить.')) {
      return
    }
    setDeleting(true)
    try {
      await deleteProject(project.id)
      onDelete?.(project.id)
    } catch (error) {
      console.error('Failed to delete project:', error)
      alert('Не удалось удалить проект')
    } finally {
      setDeleting(false)
      setMenuOpen(false)
    }
  }

  const handleStart = async () => {
    setStarting(true)
    try {
      await startProject(project.id)
      window.location.reload()
    } catch (error) {
      console.error('Failed to start project:', error)
      alert('Не удалось запустить обработку')
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium text-gray-900 truncate">
            {project.title || `Проект #${project.id}`}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {formatDate(project.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              statusColors[project.status] || 'bg-gray-100 text-gray-600'
            }`}
          >
            {statusLabels[project.status] || project.status}
          </span>
          {/* Three dots menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              disabled={deleting}
            >
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="6" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="18" r="2" />
              </svg>
            </button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {deleting ? 'Удаление...' : 'Удалить'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
        {project.source_count > 0 && (
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 012.828-2.828" />
            </svg>
            {project.source_count} {project.source_count === 1 ? 'источник' : 'источника'}
          </span>
        )}
        {project.audio_duration_seconds && (
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {formatDuration(project.audio_duration_seconds)}
          </span>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        {isCompleted ? (
          <Link
            to={`/project/${project.id}`}
            className="flex-1 text-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
          >
            Открыть транскрипт
          </Link>
        ) : isDraft && !hasSources ? (
          <Link
            to={`/new-project?continue=${project.id}`}
            className="flex-1 text-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            Добавить источники
          </Link>
        ) : isDraft && hasSources ? (
          <button
            onClick={handleStart}
            disabled={starting}
            className="flex-1 text-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {starting ? 'Запуск...' : 'Обработать'}
          </button>
        ) : project.status === 'failed' ? (
          <button
            onClick={handleStart}
            disabled={starting}
            className="flex-1 text-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {starting ? 'Запуск...' : 'Повторить'}
          </button>
        ) : project.status === 'waiting_input' ? (
          <Link
            to={`/new-project?continue=${project.id}`}
            className="flex-1 text-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-medium"
          >
            Добавить источники
          </Link>
        ) : (
          <span className="flex-1 text-center px-4 py-2 bg-gray-50 text-gray-400 rounded-lg text-sm">
            {project.status === 'processing' ? 'Обрабатывается...' : 'Ожидание'}
          </span>
        )}
      </div>
    </div>
  )
}

export default ProjectCard
